<template>
  <div class="auth-form w-100">
    <template v-if="isSuccess">
      <v-sheet class="register-success-card pa-6 text-center rounded-lg" elevation="0">
        <v-icon
          class="register-success-card__icon mb-3"
          :icon="envelopeIcon"
          size="40"
          aria-hidden="true"
        />
        <h2 class="register-success-card__title text-h6 font-weight-semibold mb-2">
          Registro exitoso
        </h2>
        <p class="register-success-card__text text-body-2 mb-4">
          Le enviamos un correo electrónico para validar su cuenta.
          Siga las instrucciones para completar la activación.
        </p>
        <v-btn
          block
          color="primary"
          @click="goBack"
        >
          Volver al inicio
        </v-btn>
      </v-sheet>
    </template>

    <template v-else>
      <v-btn
        variant="text"
        class="text-medium-emphasis px-0 mb-1 justify-start"
        :prepend-icon="backIcon"
        @click="$emit('back')"
      >
        Volver al inicio
      </v-btn>

      <h1 class="auth-form__title text-h5 font-weight-semibold mb-2">
        Registrarme
      </h1>

      <div v-if="mobileHelpHtml" class="mobile-help d-lg-none mt-4 mb-6">
        <v-expansion-panels variant="accordion" class="mobile-help__panels">
          <v-expansion-panel elevation="0" class="mobile-help__panel">
            <v-expansion-panel-title class="mobile-help__title">
              <div class="d-flex align-center min-width-0">
                <v-icon :icon="helpIcon" size="18" class="mobile-help__icon mr-2" />
                <span class="text-body-2 font-weight-semibold text-truncate">
                  ¿Necesita ayuda para crear su cuenta?
                </span>
              </div>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="mobile-help__text">
              <div class="mobile-help-html text-body-2" v-html="mobileHelpHtml" />
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </div>

      <v-form ref="authForm" @submit.prevent="handleSubmit">
        <div class="stack-fields">
          <TipoDocumentoTextField
            v-model:tipo-id="documentTipoId"
            v-model:numero="documentNumber"
            :rules-nro-doc="[rules.required]"
          />

          <v-text-field
            v-model="email"
            label="Correo electrónico"
            placeholder="ejemplo@correo.com"
            type="text"
            inputmode="email"
            variant="outlined"
            density="compact"
            hide-details="auto"
            autocomplete="off"
            name="portal-register-contact"
            :rules="[rules.required, rules.email]"
            @blur="clearDocumentAutofilledAsEmail"
          />

          <v-text-field
            v-model="password"
            label="Contraseña"
            placeholder="Ingrese su contraseña"
            :type="showPassword ? 'text' : 'password'"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :rules="[rules.required, rules.passwordPolicy]"
            autocomplete="new-password"
            name="portal-register-password"
            :append-inner-icon="showPassword ? eyeOffIcon : eyeIcon"
            @click:append-inner="showPassword = !showPassword"
          />

          <PasswordPolicyChecklist :password="password" />

          <v-text-field
            v-model="confirmPassword"
            label="Repita su contraseña"
            placeholder="Repita su contraseña"
            :type="showConfirmPassword ? 'text' : 'password'"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :rules="[rules.required, rules.sameAs(password)]"
            autocomplete="new-password"
            name="portal-register-confirm-password"
            :append-inner-icon="showConfirmPassword ? eyeOffIcon : eyeIcon"
            @click:append-inner="showConfirmPassword = !showConfirmPassword"
          />

          <div v-if="showTermsAcceptance" class="my-2">
            <v-checkbox v-model="acceptTerms" hide-details density="comfortable" class="align-start" color="primary">
              <template #label>
                <span class="text-body-2 text-medium-emphasis">
                  Estoy de acuerdo con los
                  <button
                    type="button"
                    class="auth-form__link-btn font-weight-medium text-decoration-underline"
                    @click.stop.prevent="openTermsDialog"
                  >
                    términos y condiciones
                  </button>.
                </span>
              </template>
            </v-checkbox>

            <v-dialog v-model="termsDialog" max-width="640" scrollable>
              <v-card class="legal-dialog-card">
                <v-card-title class="d-flex align-center justify-space-between flex-nowrap py-2 pl-4 pr-2 ml-1">
                  <span class="text-h6 font-weight-semibold text-truncate text-primary">
                    Términos y condiciones
                  </span>
                  <v-btn
                    variant="text"
                    :icon="closeIcon"
                    class="legal-dialog-card__close-btn flex-shrink-0"
                    aria-label="Cerrar"
                    @click="termsDialog = false"
                  />
                </v-card-title>
                <v-card-text class="legal-dialog-card__body text-body-2 pb-4 pt-0 px-4 mx-2">
                  <div class="legal-dialog-html" v-html="termsHtml" />
                </v-card-text>
              </v-card>
            </v-dialog>
          </div>

          <div class="d-flex flex-wrap gap-3 mt-2">
            <v-btn variant="outlined" type="button" @click="$emit('back')">
              Cancelar
            </v-btn>
            <v-btn
              type="submit"
              class="flex-grow-1"
              min-width="120"
              color="primary"
              :loading="isLoading"
            >
              {{ isLoading ? 'Registrando...' : 'Registrarme' }}
            </v-btn>
          </div>
        </div>
      </v-form>
    </template>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import enums from '../../utils/enums'
import rules from '../../utils/helpers/rules'
import TipoDocumentoTextField from '../shared/TipoDocumentoTextField.vue'
import PasswordPolicyChecklist from './PasswordPolicyChecklist.vue'

export default {
  name: 'RegisterAuthForm',
  components: { TipoDocumentoTextField, PasswordPolicyChecklist },
  props: {
    hospitalConfig: {
      type: Object,
      required: true,
    },
    initialTdId: {
      default: null,
      validator: (v) =>
        v === null || v === undefined || (typeof v === 'number' && Number.isInteger(v)),
    },
    initialNroDoc: {
      type: String,
      default: '',
    },
  },
  emits: ['back'],
  data() {
    return {
      backIcon: enums.icons.BACK,
      closeIcon: enums.icons.CLOSE,
      eyeIcon: enums.icons.EYE,
      eyeOffIcon: enums.icons.EYE_OFF,
      envelopeIcon: enums.icons.ENVELOPE,
      helpIcon: enums.icons.HELP_OUTLINE,
      documentTipoId: null,
      documentNumber: '',
      email: '',
      password: '',
      confirmPassword: '',
      showPassword: false,
      showConfirmPassword: false,
      acceptTerms: false,
      termsDialog: false,
      isLoading: false,
      isSuccess: false,
      emailAutofillGuardTimeouts: [],
      rules: rules,
    }
  },
  computed: {
    showTermsAcceptance() {
      return this.hospitalConfig.aceptaTerminosEnRegistro !== false
    },
    termsHtml() {
      return this.hospitalConfig.termsAndConditionsHtml ?? ''
    },
    mobileHelpHtml() {
      return String(this.hospitalConfig?.helpTextRegister ?? '').trim()
    },
  },
  watch: {
    initialTdId() {
      this.applyInitialDocument()
    },
    initialNroDoc() {
      this.applyInitialDocument()
    },
  },
  mounted() {
    this.applyInitialDocument()
    this.setupEmailAutofillGuard()
  },
  beforeUnmount() {
    this.emailAutofillGuardTimeouts.forEach(clearTimeout)
  },
  methods: {
    ...mapActions({
      setAlert: 'alerts/setAlert',
      registerAccount: 'auth/register',
    }),
    openTermsDialog() {
      this.termsDialog = true
    },
    applyInitialDocument() {
      if (this.initialTdId != null) {
        this.documentTipoId = this.initialTdId
      }
      const nroDoc = String(this.initialNroDoc ?? '').trim()
      if (nroDoc) {
        this.documentNumber = nroDoc
      }
      this.clearDocumentAutofilledAsEmail()
    },
    clearDocumentAutofilledAsEmail() {
      const email = String(this.email ?? '').trim()
      const nroDoc = String(this.documentNumber ?? this.initialNroDoc ?? '').trim()
      if (email && (email === nroDoc || this.looksLikeCredentialUsername(email))) {
        this.email = ''
      }
    },
    looksLikeCredentialUsername(value) {
      const text = String(value ?? '').trim()
      if (!text) return false
      if (text.includes('@')) return false
      return /^\d+$/.test(text)
    },
    setupEmailAutofillGuard() {
      this.emailAutofillGuardTimeouts.forEach(clearTimeout)
      this.emailAutofillGuardTimeouts = []
      const delays = [0, 50, 100, 200, 400, 800, 1600]
      delays.forEach((ms) => {
        this.emailAutofillGuardTimeouts.push(
          setTimeout(() => this.clearDocumentAutofilledAsEmail(), ms)
        )
      })
    },
    goBack() {
      this.isSuccess = false
      this.documentTipoId = null
      this.documentNumber = ''
      this.email = ''
      this.password = ''
      this.confirmPassword = ''
      this.acceptTerms = false
      this.$emit('back')
    },
    async handleSubmit() {
      const { valid } = await this.$refs.authForm.validate()
      if (!valid) return
      if (this.password !== this.confirmPassword) {
        this.setAlert({
          type: 'warning',
          message: 'Las contraseñas no coinciden',
        })
        return
      }
      if (this.showTermsAcceptance && !this.acceptTerms) {
        this.setAlert({
          type: 'warning',
          message: 'Debe aceptar los términos y condiciones para continuar',
        })
        return
      }
      if (this.documentTipoId == null) {
        this.setAlert({
          type: 'warning',
          message: 'Seleccione el tipo de documento.',
        })
        return
      }
      this.isLoading = true
      const payload = {
        tdId: this.documentTipoId,
        nroDoc: this.documentNumber,
        email: this.email,
        password: this.password,
      }
      try {
        const result = await this.registerAccount(payload)
        if (result?.ok) {
          this.isSuccess = true
          return
        }
        this.setAlert({
          type: 'warning',
          message: this.getRegisterErrorMessage(result),
        })
      } finally {
        this.isLoading = false
      }
    },
    getRegisterErrorMessage(result) {
      const code = String(result?.errorCode ?? '').toLowerCase()
      if (code === 'edad_minima_portal_no_cumplida') {
        return 'No cumple con la edad mínima requerida para registrarse en el portal.'
      }
      if (result?.message) return result.message
      if (code === 'portal_no_habilitado') {
        return 'No se encuentra habilitado para registrarse en el portal.'
      }
      if (code === 'usuario_ya_registrado') {
        return 'La ficha ya tiene una cuenta registrada.'
      }
      return 'No se pudo registrar la cuenta. Verifique los datos e intente nuevamente.'
    },
  },
}
</script>

<style scoped>
.auth-form {
  max-width: 28rem;
}
.auth-form__title {
  color: var(--portal-primary);
}
.auth-form__link {
  color: var(--portal-primary);
}
.mobile-help__panel {
  --mobile-help-bg: rgba(var(--v-theme-primary), 0.035);
  border: 1px solid rgba(var(--v-theme-primary), 0.075);
  border-radius: 8px !important;
  color: var(--portal-primary);
  overflow: hidden;
  background: var(--mobile-help-bg) !important;
  box-shadow: none !important;
}
.mobile-help__title {
  min-height: 44px !important;
  padding: 0 14px !important;
  color: var(--portal-primary);
  background: var(--mobile-help-bg);
}
.mobile-help__title :deep(.v-expansion-panel-title__overlay) {
  opacity: 0;
}
.mobile-help__icon {
  color: var(--portal-primary);
}
.mobile-help__text :deep(.v-expansion-panel-text__wrapper) {
  padding: 12px 14px 16px;
  border-top: 1px solid rgba(var(--v-theme-primary), 0.06);
  background: var(--mobile-help-bg);
}
.mobile-help-html {
  color: #000;
  line-height: 1.55;
}
.portal-app--dark .mobile-help-html {
  color: rgba(var(--v-theme-on-surface), 0.78);
}
.mobile-help-html :deep(p) {
  margin-bottom: 0.5rem;
}
.mobile-help-html :deep(p:last-child) {
  margin-bottom: 0;
}
.mobile-help-html :deep(ul) {
  list-style: disc outside;
  padding-left: 1.25rem;
  margin: 0.75rem 0 0;
}
.mobile-help-html :deep(li) {
  margin-bottom: 0.25rem;
}
.mobile-help-html :deep(strong) {
  font-weight: 600;
}
.register-success-card {
  background-color: rgba(var(--v-theme-primary), 0.12) !important;
}
.register-success-card__icon {
  color: var(--portal-primary);
  opacity: 0.95;
}
.register-success-card__title,
.register-success-card__text {
  color: var(--portal-primary);
}
.stack-fields > * + * {
  margin-top: 1.25rem;
}
.gap-3 {
  gap: 0.75rem;
}
.auth-form__link-btn {
  display: inline;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  color: var(--portal-primary);
  text-align: inherit;
}
.legal-dialog-card__body {
  max-height: min(70vh, 520px);
}
.legal-dialog-html :deep(ul) {
  list-style-type: disc;
}
</style>
